/**
 * Public contract between the wizard UI and any backend (mock or real).
 *
 * The mock service (`MockAgentBuilderService`) implements this against
 * localStorage; the real service (`RealAgentBuilderService`) will implement
 * it against studio-api in Phase C. Components inject the
 * `AGENT_BUILDER_SERVICE` token and don't know which impl is bound.
 *
 * Convention (P1-G1, accepted risk): when you change a response shape on
 * either side, update BOTH the mock and the real impl in the same PR.
 */

import { Observable } from 'rxjs';

// ── Wizard input shape ─────────────────────────────────────────────────

export interface WizardIdentity {
  agent_id: string;
  agent_dir: string; // derived from agent_id.lower()
  display_name: string;
  description: string;
  department: string;
  keywords: string[];
  /** Optional hub slash command people type to reach this agent in Agent Hub
   *  (e.g. "/deal_inspection"). Lowercase letters/digits/underscores, 2–48 chars
   *  after the "/", not a hub built-in (/home /agents /stay /help /switch).
   *  Empty = the default "/<agent id lowercased>". Validated by
   *  renderer.validate; uniqueness is enforced at deploy (catalog writer). */
  hub_command?: string;
  /** ROI mandate: minutes of manual work ONE session with this agent replaces
   *  (5..480). REQUIRED — renderer.validate blocks publish without it.
   *  null = not set yet (new drafts start unset; no silent default). */
  time_saved_minutes_per_session?: number | null;
  /** Short justification for the value; REQUIRED above 240 minutes. */
  time_saved_basis?: string;
  /** Profile accent (AGENT_COLORS) — avatar tint on cards & breadcrumb.
   *  UI-only: studio-api stores identity verbatim and the renderer ignores
   *  unknown identity keys (same convention as meta.visibility). */
  color?: string;
}

/** Where a scheduled automation delivers each run's result.
 *  - 's3'       saves the report to the reports bucket (share link only)
 *  - 'email'    emails the full report text directly — no S3 upload (for short/greeting emails)
 *  - 's3+email' uploads to S3, then emails a summary + report link
 *  - 'teams'    posts the full report text to Teams directly — no S3 upload
 *  - 's3+teams' uploads to S3, then posts a summary + report link to Teams */
export interface WizardDelivery {
  kind: 's3' | 'email' | 's3+email' | 'teams' | 's3+teams';
  to?: string[];
  subject?: string;
  /** Teams Incoming-Webhook URL (https://...webhook.office.com/...). */
  teams_webhook?: string;
  /** Per-recipient fan-out (email kinds only): "from_report" makes each run
   *  send one email per ---RECIPIENT:--- block the agent's output names (e.g.
   *  one section per manager); "per_recipient" is the STRICT variant for
   *  sensitive data — the platform partitions the data and addresses each
   *  email in code (configured via `per_recipient`, authored by the
   *  co-builder). In both, the static `to` list becomes the optional digest
   *  audience. Absent = "static", today's behavior. */
  recipient_mode?: 'static' | 'from_report' | 'per_recipient';
  /** Deterministic fan-out config (recipient_mode "per_recipient" only):
   *  the config-bound data source, the partition field, render mode and the
   *  optional manager rollup. Authored by the co-builder; the wizard only
   *  surfaces it. Mirrors the renderer's per_recipient contract. */
  per_recipient?: WizardPerRecipient;
}

/** Who a per-recipient fan-out addresses (per_recipient.audience.source):
 *  - people_in_data (default): one email per person found in the rows;
 *  - owner: ONE email to the agent owner with every row ("Only me");
 *  - team: the owner's (or manager_email's) direct reports from the
 *    directory — every member (rule "all") or only those with no rows
 *    (rule "not_in_data", the reminder pattern). */
export type PerRecipientAudienceSource = 'people_in_data' | 'owner' | 'team';
export type PerRecipientAudienceRule = 'all' | 'not_in_data';
/** How a person value in the data is resolved to an address: it IS an email,
 *  or it is a display name looked up through `directory_tool`. */
export type PersonFieldKind = 'email' | 'display_name';

/** Deterministic fan-out config (recipient_mode "per_recipient"). The "Data
 *  & people" step reads/writes it; the co-builder may also write it. Mirrors
 *  the renderer's per_recipient contract (automations v2 wave 1). */
export interface WizardPerRecipient {
  data_source?: {
    tool?: string;
    args?: Record<string, unknown>;
    /** Team audience only: call the tool once per member with
     *  {{person.email}} / {{person.name}} / {{person.first_name}} substituted
     *  into string args. */
    per_person?: boolean;
  };
  rows_path?: string;
  /** The PERSON FIELD path inside each row (e.g. fields.assignee.emailAddress). */
  partition_by?: string;
  render?: 'llm' | 'template';
  template?: { body?: string };
  fields?: string[];
  rollup?: { enabled?: boolean; roster_tool?: string; manager_path?: string };
  /** How partition_by values resolve to people; default email (today). */
  person_field?: { kind?: PersonFieldKind };
  /** Tried per row when partition_by yields nothing. */
  person_fallback?: { path?: string; kind?: PersonFieldKind };
  /** Directory lookup tool — required when any kind is display_name. */
  directory_tool?: string;
  audience?: {
    source?: PerRecipientAudienceSource;
    /** team: defaults to the agent owner. */
    manager_email?: string;
    /** team only; default all. */
    rule?: PerRecipientAudienceRule;
  };
  /** Attach team totals / rank to each person's slice. */
  team_context?: boolean;
}

/** One addressed email of a per-recipient run or preview. */
export interface RecipientResult {
  to: string;
  /** individual | rollup */
  kind: string;
  /** sent | skipped_unmatched | skipped_ambiguous | failed | preview … */
  status: string;
  rows: number;
}

export interface RecipientCounts {
  sent?: number;
  skipped?: number;
  failed?: number;
}

/** A REAL rendered email from a dry run (per-recipient automations). */
export interface EmailPreview {
  to: string;
  kind: 'individual' | 'rollup';
  rows: number;
  body: string;
}

/** How an automation is triggered:
 *  - schedule: an EventBridge rate()/cron() cadence (the default);
 *  - event: fired by a domain event the agent's prompt describes;
 *  - s3: fired when a file arrives under a watched prefix (s3_prefix). */
export type TriggerType = 'schedule' | 'event' | 's3';

export interface WizardShape {
  has_tools: boolean;
  tools_via_gateway: boolean;
  gateway_target: string | null;
  has_report_artifact: boolean;
  /** Agent accepts uploaded files in chat — the live panel shows an attach
   *  button and the renderer tells the agent to treat `[File: …]` blocks as
   *  provided context. */
  has_file_upload?: boolean;
  /** Agent answers from a builder-curated document set (self-service RAG).
   *  Flag-only on the client: the `knowledge-read` tool is injected
   *  server-side at render time — never pushed into WizardInput.tools. The
   *  wizard reveals a "Knowledge base" step for uploading documents. */
  has_knowledge_base?: boolean;
  has_rolling_history: boolean;
  has_rate_limit: boolean;
  custom_actions: string[];
  /** "automation" renders the scheduled (async) shape; absent/"chat" = chat. */
  agent_kind?: 'chat' | 'automation';
  /** What fires the automation. Absent defaults to 'schedule' so existing
   *  drafts (which only ever had a schedule_expression) are unchanged. */
  trigger_type?: TriggerType;
  /** EventBridge rate()/cron() — schedule trigger only; hourly is the rate() floor. */
  schedule_expression?: string;
  /** s3 trigger only: the bucket key prefix to watch ("reports/inbound/"). */
  s3_prefix?: string;
  delivery?: WizardDelivery;
  /** Complex agent: an LLM generates the custom logic files (Phase 3). */
  needs_custom_code?: boolean;
}

export interface WizardPrompt {
  role: string;
  instructions: string;
  /** Automation only: what the agent does on each scheduled run. */
  task?: string;
  /** Hero AI-draft extras — returned by ai-assist/prompt, not persisted on the draft. */
  display_name?: string;
  /** Refined one-sentence catalog description (not a verbatim copy of the user prompt). */
  description?: string;
  is_automation?: boolean;
  schedule_expression?: string;
  /** Automation only: where each run's result is delivered. The S3-bearing
   *  kinds (s3 / s3+email / s3+teams) write a durable report artifact and pull
   *  in the report-template builder; email/teams are plain notifications. */
  delivery_kind?: 'email' | 's3' | 'teams' | 's3+email' | 's3+teams';
  /** Automation only: explicit recipient addresses for email/s3+email delivery,
   *  extracted when the user names a specific address in their description. */
  delivery_to?: string[];
  /** Automation only: explicit email subject, when the user specifies one. */
  delivery_subject?: string;
  /** Set whenever the agent's job is to produce a report/document as its
   *  output — a one-off chat answer OR a scheduled automation — independent
   *  of is_automation/delivery_kind: the LLM's inferred report format ("as an
   *  Excel/spreadsheet" → "xlsx", "as a deck/slides" → "pptx", "as a PDF" →
   *  "pdf", else "html"). Preferred over the client-side detectFormat() regex
   *  when present — it understands phrasing the regex doesn't. null when the
   *  agent is a plain conversational/action assistant with no report output.
   *  A non-automation agent with this set gets has_report_artifact equipped
   *  directly (withReportShape in create-agent.service.ts) — automations
   *  still gate has_report_artifact on delivery_kind via deliveryWritesReport. */
  report_format?: 'html' | 'pdf' | 'xlsx' | 'pptx';
  /** Companion to report_format: a short section outline derived from the
   *  description (e.g. requested column names → a data-table section). */
  report_sections?: string[];
  abilities?: string[];
  /** LLM-generated routing keywords (ai-assist/prompt). Applied to
   *  identity.keywords so AgentHome can route to the agent. */
  keywords?: string[];
}

/** Complex agents: what the generated custom logic should do, in the
 *  creator's words. The codegen runtime works from this + the tool
 *  implementation notes. */
export interface WizardCustomCode {
  logic_description: string;
}

export interface AnthropicToolSchema {
  name: string;
  description: string;
  input_schema: {
    type: 'object';
    properties: Record<string, unknown>;
    required?: string[];
  };
  /** Gateway target this tool routes through. Targets may mix across the
   *  equipped set; entries without one (legacy drafts, custom tools) fall
   *  back to shape.gateway_target. Stripped from the schemas the model sees. */
  target?: string;
  /** Complex agents: how a CUSTOM tool should be implemented (plain language).
   *  Persisted with the draft and fed to the code generator; stripped from the
   *  schemas the model sees in test-chat / rendered tools.py. */
  implementation_notes?: string;
}

export interface WizardIamOverrides {
  extra_statements: Array<Record<string, unknown>>;
}

export type AgentVisibility = 'me' | 'dept' | 'everyone';

/** Report template config for report agents (shape.has_report_artifact).
 *  The renderer turns this into a deterministic "Report output" block in the
 *  system prompt and a report contract in agent_meta.json. */
/** A Markdown-report section: either a plain text heading (string, the legacy
 *  shape) or a typed object. When `chartKind` is set the section renders as a
 *  graph of that kind (the skeleton emits a ```chart block under the heading);
 *  absent → a normal prose section. Strings and objects may be mixed. */
export interface ReportSection {
  title: string;
  chartKind?: DashboardChartKind;
  /** Optional creator-authored guidance for this section — shown in the
   *  collapsible description panel in the builder and sent to the co-builder
   *  so the model knows what content each section should contain. */
  description?: string;
  /** Explicit column headers for a chartKind: 'table' section (e.g. ["Date",
   *  "SKU", "Summary"]) — set by the co-builder when the creator names them,
   *  so the workbook/table preview shows the real headers instead of the
   *  generic Name/Value placeholder before the agent has ever run. Ignored
   *  for non-table sections; the agent's actual run output is unaffected —
   *  this only seeds what the STATIC preview scaffold displays. */
  columns?: string[];
}

export interface WizardReportConfig {
  title?: string;
  /** Ordered report sections. Accepts legacy plain strings (text headings) and
   *  typed ReportSection objects (text or graph). Normalize via
   *  normalizeReportSections() before use. */
  sections?: Array<string | ReportSection>;
  /** 'html'/'pdf' → a Markdown report styled into a document. 'dashboard' → a
   *  grid of chart widgets (see `dashboard`); the agent queries data per section
   *  and emits a dashboard spec the studio renders to charts. */
  format?: 'html' | 'pdf' | 'dashboard' | 'xlsx' | 'pptx';
  /** Dashboard spec — used only when format === 'dashboard'. A parallel, typed
   *  sibling of `sections` (which stays a plain string[] for Markdown reports). */
  dashboard?: WizardDashboardConfig;
  /** Natural-language description of the desired report format/layout, written
   *  by the creator. Drives the derived title + section outline; the agent
   *  produces the report as Markdown at run time, styled by the report theme. */
  description?: string;
  /** Editable Markdown template (with placeholders) the agent fills each run.
   *  Derived from title + sections by default; the creator can hand-tune it.
   *  Empty → the renderer derives a skeleton from title + sections. */
  markdown?: string;
  /** Custom report theme CSS. Empty → the built-in default theme. A preset
   *  picker writes a known theme here; "Advanced" lets the creator edit it.
   *  Sanitized before it's inlined into the downloadable/emailed document. */
  css?: string;
  /** Opt out of the WatchGuard letterhead/footer branding in the generated
   *  document. Undefined/false → branding shown (the default). */
  hideBranding?: boolean;
}

/** Email-delivery template for scheduled automations that deliver by email
 *  (delivery.kind 'email' or 's3+email'). Stored at meta.email and mirrored
 *  render-side by the delivery template (renderer/_templates_async.py) exactly
 *  like meta.report. Empty/absent → the built-in default WatchGuard themed
 *  email is used, so every scheduled email is branded with no creator action.
 *  The creator (or the Co-builder) customizes the subject, the body layout and
 *  the theme, mirroring the report-template builder. */
export interface WizardEmailConfig {
  /** Subject line. Empty → falls back to delivery.subject, then a default of
   *  "<agent display name> report". Supports the same run-time placeholders as
   *  `markdown` (e.g. {{date}}, {{agent_name}}). */
  subject?: string;
  /** Editable Markdown body template the renderer wraps in the themed email
   *  shell each run. Placeholders filled at delivery time:
   *    {{report}}     — the run's full report text (email-only kind)
   *    {{summary}}    — the model's short notification body (s3+email)
   *    {{report_url}} — the S3 share link (s3+email; absent for email-only)
   *    {{agent_name}} — the agent's display name
   *    {{date}}       — the run date
   *  Empty → the renderer uses a default body that presents the report/summary
   *  under a branded header with the link (when present). */
  markdown?: string;
  /** Custom email theme CSS. Empty → the built-in default WatchGuard email
   *  theme. A preset picker writes a known theme here; "Advanced" lets the
   *  creator hand-edit it. Sanitized before it's inlined into the sent email. */
  css?: string;
  /** Opt out of the WatchGuard letterhead/footer branding in the email shell.
   *  Undefined/false → branding shown (the default). */
  hideBranding?: boolean;
}

/** Per-agent domain filters for the WebSearch tool (meta.websearch). AWS's Web
 *  Search connector only supports a global, deploy-time denylist and has no
 *  allowlist concept at all, so the studio-api renderer bakes both lists into
 *  the generated tools.py and post-filters results client-side at run time —
 *  see wg-agents-studio-api's renderer.py/_templates.py. Only meaningful when
 *  a WebSearch tool is equipped; empty/absent → no per-agent filtering (the
 *  global admin denylist still applies). */
export interface WizardWebsearchConfig {
  /** Bare hostnames (e.g. "wikipedia.org", never a full URL) results are
   *  restricted to (plus their subdomains). Empty/absent → no allowlist. */
  allowedDomains?: string[];
  /** Bare hostnames results are hidden from, on top of any admin-wide
   *  denylist. Empty/absent → no per-agent blocklist. */
  blockedDomains?: string[];
}

/** A full email template generated by the AI from a plain-English description:
 *  a subject, an editable Markdown body skeleton, and a cohesive CSS theme.
 *  Applied wholesale to meta.email by the wizard (mirrors ReportTemplateResult). */
export interface EmailTemplateResult {
  subject: string;
  markdown: string;
  css: string;
}

/** Chart kinds a dashboard section can render. */
export type DashboardChartKind = 'kpi' | 'bar' | 'line' | 'pie' | 'table';

/** One dashboard widget: a chart bound to a single data-tool query. The agent
 *  runs the query at run time and fills the section's data; the studio renders
 *  it (live in the hub panel, or as inline-SVG in a scheduled-run document). */
export interface WizardDashboardSection {
  /** Stable id (referenced by interactive filter/drill-down actions). */
  id: string;
  title: string;
  kind: DashboardChartKind;
  /** Which data tool feeds this section. tool_wireName is the UN-prefixed
   *  operationId (== tools[].name), NEVER the catalog's target___op name. */
  query?: { tool_wireName?: string; args?: Record<string, unknown> };
  /** How the tool's return maps to the chart, e.g. { x: 'key', y: 'count' }. */
  encoding?: Record<string, string>;
}

export interface WizardDashboardConfig {
  sections: WizardDashboardSection[];
}

/** A full report template generated by the AI from a plain-English description:
 *  a title, an ordered section outline, an editable Markdown skeleton, and a
 *  cohesive CSS theme. Applied wholesale to meta.report by the wizard. */
export interface ReportTemplateResult {
  title: string;
  sections: Array<string | ReportSection>;
  format: 'html' | 'pdf' | 'xlsx' | 'pptx';
  markdown: string;
  css: string;
}

/** A domain starter template for the downloadable report: pick one, then tweak
 *  sections directly or ask the Co-builder. Outcome-first — creators start
 *  from a recognisable report shape instead of a blank sections list.
 *  Server-owned (GET /report-templates); the UI bundles a fallback copy in
 *  shared/data/report-template-presets.ts for offline/old-API. */
export interface ReportTemplatePreset {
  key: string;
  /** Short chip label. */
  name: string;
  /** One-line tooltip: who this report is for. */
  blurb: string;
  title: string;
  sections: (string | ReportSection)[];
}

export interface WizardMeta {
  renderer: string | null;
  capabilities: string[];
  required_inputs: string[];
  suggested_actions: Array<{ label: string; prompt: string }>;
  /** Who can find/use the agent once live (redesign "Access" section).
   *  UI-only for now: the studio-api renderer ignores unknown meta keys. */
  visibility?: AgentVisibility;
  /** Group ids the builder wants to SHARE this agent with. On the live
   *  transition studio-api emits a PENDING_AGENT_SHARE request for these; an
   *  admin approves it (grant + visibility flip). Empty/absent → stays private
   *  ("just me"). Studio writes this verbatim; the renderer ignores it. */
  share_groups?: string[];
  report?: WizardReportConfig;
  /** Email-delivery template for automations delivering by email / s3+email.
   *  Empty/absent → the built-in default WatchGuard themed email. Mirrors
   *  `report`; merged subfield-by-subfield by the co-builder (RECURSE_PATHS). */
  email?: WizardEmailConfig;
  /** WebSearch tool domain allow/block lists. Empty/absent → no per-agent
   *  filtering. Mirrors `report`/`email`; merged subfield-by-subfield by the
   *  co-builder (RECURSE_PATHS). */
  websearch?: WizardWebsearchConfig;
  /** Automation drafts seeded from the hero: the cadence was inferred from a
   *  plain-English description (or defaulted), so the builder asks the creator
   *  to confirm the schedule (in UTC) before submit. Cleared once they touch
   *  the Schedule section. UI-only — the renderer ignores unknown meta keys. */
  schedule_unconfirmed?: boolean;
  /** How identity.keywords were set. "user" = the creator typed them (the
   *  backend then preserves them at publish); anything else (default) lets
   *  publish regenerate them from the agent's purpose. UI-only meta key. */
  keywords_source?: 'user' | 'auto';
}

export interface WizardInput {
  identity: WizardIdentity;
  shape: WizardShape;
  prompt: WizardPrompt;
  tools: AnthropicToolSchema[];
  iam_overrides: WizardIamOverrides;
  meta: WizardMeta;
  /** Deploy targets this agent has been published to: ["dev"] | ["prod"] |
   *  ["dev","prod"]. Set by the studio-api per publish; absent ⇒ dev-only. */
  environments?: string[];
  /** Complex agents only (shape.needs_custom_code). */
  custom_code?: WizardCustomCode;
}

// ── Gateway tool catalog (W1 — picker source) ──────────────────────────
// Published per target into GATEWAY_TARGET_REGISTRY and served by GET /tools.
// Carries the Anthropic `input_schema` so a picked tool drops straight into
// WizardInput.tools[] for the renderer.

/** Plain-English copy published with the tool by its gateway team
 *  (wg-agents-gateways tool-schema.json "friendly" block). The Abilities
 *  catalog prefers this over the UI's local fallback map. */
export interface GatewayToolFriendly {
  name?: string;
  system?: string;
  description?: string;
  example?: string;
  badge?: string;
  category?: string;
  /** Optional sub-group within the connector (e.g. "BambooHR (HR)") — shown as
   *  a chip in the picker so a grab-bag connector reads as data domains. */
  group?: string;
  popular?: boolean;
  /** wg-icon name (unknown names render blank — safe). */
  icon?: string;
  /** Display rank within the tool's category — ascending; unset/0 sorts last. */
  order?: number;
}

export interface GatewayTool extends AnthropicToolSchema {
  operationId?: string; // un-prefixed name the model calls (execute_tool adds the target prefix)
  displayName?: string;
  sensitivity?: string;
  friendly?: GatewayToolFriendly;
}

export interface GatewayTarget {
  target: string;
  tools: GatewayTool[];
}

/** Server-owned arrangement contract for the tool catalog (GET /tools
 *  `presentation`) — the same document the admin portal edits. Absent when
 *  the deployed studio-api predates it; the UI then falls back to its bundled
 *  ABILITY_CATEGORIES order. */
export interface ToolCatalogPresentation {
  /** Category display order for the Abilities surfaces. Categories present in
   *  the data but missing here still render — appended after the known ones. */
  category_order: string[];
  /** Category for tools whose friendly block doesn't set one. */
  default_category: string;
  /** Sort keys within a category, e.g. ['order', 'name']. */
  sort_within_category: string[];
}

/** Full GET /tools payload: the published targets plus the presentation
 *  contract. listTools() keeps returning just the targets for callers that
 *  don't arrange anything. */
export interface ToolCatalogResult {
  targets: GatewayTarget[];
  presentation?: ToolCatalogPresentation;
  /** Authoritative KB creator-grant (server `knowledge_base_granted`) — the
   *  same usability gate the KB upload/test-run enforces. Undefined on an older
   *  API; callers then fall back to catalog inference (knowledgeBaseGranted). */
  knowledgeBaseGranted?: boolean;
}

/** A group the builder may request to share an agent with (Access step picker).
 *  id + display only — choosing a target grants nothing; the grant is a
 *  super_admin approval in admin-api. From GET /agents/groups (group_directory). */
export interface GroupOption {
  group_id: string;
  name: string;
  description: string;
}

// ── Abilities library (discover what's available + roadmap + gaps) ──────
// The library page browses the live catalog above PLUS a curated roadmap of
// connectors that aren't live yet, and lets people signal demand for gaps.

/** A connector on the roadmap but not yet available — a curated list owned by
 *  studio-api (GET /abilities/coming-soon). Shaped like the gateway `friendly`
 *  block + an ETA. It is NEVER a runnable catalog tool, so it can't be equipped
 *  or granted — purely a "here's what's coming" signal in the library. */
export interface ComingSoonAbility {
  id: string;
  name: string;
  /** Source system in plain words, e.g. "BambooHR". */
  system: string;
  desc: string;
  badge: string;
  category: string;
  /** wg-icon name (unknown names render blank — safe). */
  icon: string;
  /** Plain-language availability, e.g. "In review", "Next quarter". */
  eta: string;
  status: 'coming_soon';
}

/** A user's request for a missing/coming-soon connector
 *  (POST /abilities/requests). Durably recorded so admins see demand. */
export interface AbilityRequest {
  /** Free-text search term that found nothing (no-match request). */
  term?: string;
  /** Source system name (a coming-soon "Notify me"). */
  system?: string;
  /** Coming-soon ability id, when notifying about a specific one. */
  ability_id?: string;
  /** Where it originated: 'search' | 'coming_soon' | 'wizard_search'. */
  source: string;
  /** What the requester would build with it — the triage context admins
   *  prioritize on (request-ability dialog; ≤2000 chars server-side). */
  use_case?: string;
  /** When they need it: 'asap' | 'month' | 'quarter' | 'exploring'. */
  timeframe?: string;
}

// ── Knowledge base (self-service RAG, draft-scoped) ────────────────────
// Builders upload documents the agent answers from (shape.has_knowledge_base).
// Files go browser→S3 via a presigned PUT minted by studio-api
// (POST /agents/drafts/{id}/knowledge/uploads), then a confirm call registers
// them; ingestion is asynchronous — the wizard polls per-doc status.

/** Server-owned ingestion lifecycle of one uploaded document. */
export type KnowledgeDocStatus =
  | 'pending_upload' // ticket minted, bytes not confirmed yet
  | 'queued'         // confirmed, waiting for the ingestion worker
  | 'ingesting'      // being parsed/chunked/embedded
  | 'ready'          // fully searchable
  | 'partial'        // searchable, but some pages/sections failed
  | 'failed'         // not searchable — see status_detail
  | 'deleting';      // removal in progress

export interface KnowledgeDocument {
  doc_id: string;
  filename: string;
  title: string;
  size_bytes: number;
  status: KnowledgeDocStatus;
  /** Human-readable detail for partial/failed (e.g. "12 pages could not be parsed"). */
  status_detail?: string;
  uploaded_at: number; // unix-seconds (matches the RelativeTimePipe input)
  uploaded_by: string;
}

/** Per-draft document budget, returned with every list. */
export interface KnowledgeQuota {
  max_docs: number;
  max_total_bytes: number;
  max_file_bytes: number;
  used_docs: number;
  used_bytes: number;
}

/** GET /agents/drafts/{id}/knowledge/documents response body. */
export interface KnowledgeDocumentList {
  documents: KnowledgeDocument[];
  quota: KnowledgeQuota;
}

/** POST /agents/drafts/{id}/knowledge/uploads response: a short-lived
 *  presigned S3 PUT. `headers` (when present) must be echoed on the PUT —
 *  they're signed into the URL. The PUT itself must NOT carry the app's
 *  Authorization header (it would break the SigV4 signature). */
export interface KnowledgeUploadTicket {
  doc_id: string;
  upload_url: string;
  headers?: Record<string, string>;
  max_bytes: number;
}

// ── State machine (per Phase F1 + P1-G12) ──────────────────────────────

export type DraftState =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'publishing'
  | 'pr_open'
  | 'partial_merge'   // P1-G12: PR1 merged, PR2 still open
  | 'building'
  | 'deploying'
  | 'live'
  | 'promoted'        // cross-env promote merged; target-env rollout confirmed in that env's Studio
  | 'failed'
  | 'broken'          // PR2 merged but PR1 wasn't — requires investigation
  | 'rejected'
  | 'withdrawn'
  | 'editing'
  | 'deleting';

export interface AuditEntry {
  actor: string;
  action: string;
  ts: number; // unix-seconds
  comment?: string;
}

export interface ErrorContext {
  failed_step: string;
  error_message: string;
  completed_steps: string[];
  occurred_at: number;
}

/** One server-computed submission check (studio-api submission_checks).
 *  Computed at submit time and persisted on the draft; the approvals queue
 *  renders these instead of client-derived guesses when present. */
export interface SubmissionCheck {
  label: string;
  ok: boolean;
  note?: string;
}

// ── Codegen (complex agents, Phase 3) ──────────────────────────────────
// Lifecycle of the LLM-generated custom logic. Lives on the Draft as a
// server-owned attribute: the worker Lambda writes it, the UI polls getDraft
// while status is queued/running. `stale` is annotated server-side on reads
// (true when the spec changed after generation — regenerate before publish).

export type CodegenStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface CodegenReviewFinding {
  severity: 'blocker' | 'warn' | 'info';
  file: string;
  line?: number;
  message: string;
}

export interface CodegenReview {
  verdict: 'pass' | 'fail';
  summary: string;
  findings: CodegenReviewFinding[];
}

export interface DraftCodegen {
  status: CodegenStatus;
  run_id: string;
  requested_by: string;
  requested_at: number; // unix-seconds
  completed_at?: number;
  spec_hash?: string;
  /** Generated logic files (path -> content). Kept through failed
   *  regenerations so the last good code stays visible. */
  files?: Record<string, string>;
  extra_requirements?: string[];
  notes?: string;
  self_check?: { attempts: number; ast_ok: boolean };
  review?: CodegenReview;
  feedback?: string;
  feedback_history?: Array<{ feedback: string; by: string; ts: number }>;
  error?: string;
  error_details?: string[];
  /** Server-annotated on reads: generated code no longer matches the spec. */
  stale?: boolean;
}

// ── Automation operations (run history / Run now / kill switch) ────────
// Live automations are headless: runs + delivery ARE the product surface.
// Run records are written by the agent's trigger Lambda (RUNS# partition);
// GET /agents/published/{agent_id}/runs returns them with the schedule state.

export type AgentRunStatus = 'started' | 'done' | 'error';

export interface AgentRun {
  run_id: string;
  status: AgentRunStatus;
  trace_id?: string;
  /** "schedule", or "user:<email>" for Run now. */
  triggered_by?: string;
  started_at?: number; // unix-seconds
  finished_at?: number;
  duration_ms?: number;
  channels?: string[];
  report_url?: string;
  /** Why email was skipped/failed when it was requested (S3 still has the report). */
  delivery_note?: string;
  error?: string;
  /** Server-annotated terminal detail for a 'started' row that never reported
   *  a result: 'timed_out' (the run exceeded its budget) or 'reconciled' (the
   *  reconciler closed a run the trigger Lambda left dangling). Surfaced as a
   *  distinct status pill so a stuck run never looks like it's still going. */
  state_detail?: 'timed_out' | 'reconciled';
  /** Per-recipient automations: who the run addressed (capped at 100 by the
   *  trigger; no bodies) and the sent/skipped/failed tally. Absent otherwise. */
  recipients?: RecipientResult[];
  recipient_counts?: RecipientCounts;
}

export type ScheduleState = 'ENABLED' | 'DISABLED' | 'unknown';

export interface AgentRunsResult {
  runs: AgentRun[];
  schedule_state: ScheduleState;
  /** Durable pause flag from the runs response. The schedule_state reflects
   *  the EventBridge rule (eventually consistent / best-effort), but `paused`
   *  is the source of truth the agent itself honors — prefer it for the
   *  Pause/Resume control so the button never lies about whether runs fire. */
  paused?: boolean;
}

// ── Automation preview run (draft test-run) ────────────────────────────
// The async agent's answer to test-chat: one REAL execution of the draft's
// task (all gateway tools, including writes, execute for real; no delivery
// side effects — email/upload/Teams delivery is simulated) run by a worker
// Lambda. Lives on the Draft as a server-owned attribute like codegen:
// POST test-run returns 202, the UI polls getDraft.

export interface DraftTestRun {
  status: CodegenStatus;
  run_id: string;
  requested_by?: string;
  requested_at?: number; // unix-seconds
  started_at?: number;
  completed_at?: number;
  duration_ms?: number;
  /** The report the scheduled run would have delivered (S3 content for s3/* kinds). */
  report_text?: string;
  /** For s3+email / s3+teams: the model-generated notification body (2-4 sentences).
   *  Absent for s3-only, email-only, or teams-only kinds. For per-recipient
   *  runs this is email_previews[0].body (compat). */
  email_body?: string;
  /** Per-recipient dry run: the REAL rendered emails (≤ 8, bodies truncated)
   *  and the full audience including skipped people. */
  email_previews?: EmailPreview[];
  recipients?: RecipientResult[];
  tool_calls?: Array<{ name: string; input: unknown; output?: unknown; error?: string;
                      write?: boolean; connector?: string }>;
  model_rounds?: number;
  /** Present when the previewed agent equips write tools (real actions). */
  write_notice?: WriteActionNotice | null;
  error?: string;
}

export interface Draft {
  id: string;
  version: number; // optimistic locking (P1-G17)
  state: DraftState;
  created_by: string;
  /** Stable identity across revisions (server-assigned; a revision inherits
   *  its source's). Absent on drafts that predate lineage tracking. */
  lineage_id?: string;
  created_at: number; // unix-seconds
  last_modified: number;
  inputs: WizardInput;
  // Approval workflow fields (post-submit)
  submitted_by?: string;
  submitted_at?: number;
  submission_checks?: SubmissionCheck[];
  approver?: string;
  decision_at?: number;
  reviewer_comments?: string;
  audit_trail?: AuditEntry[];
  // Publishing fields (post-approve)
  pr1_url?: string;
  /** Real PR number (W5) — what refresh-status polls for the merge. */
  pr1_number?: number;
  pr2_url?: string;
  publish_commit_sha?: string;
  published_at?: number;
  /** W5 lifecycle (server-owned): set on the live transition. */
  live_at?: number;
  runtime_arn?: string;
  error_context?: ErrorContext;
  // Codegen state (complex agents only; server-owned)
  codegen?: DraftCodegen;
  // Preview-run state (automation drafts only; server-owned)
  test_run?: DraftTestRun;
}

/** Which record an ownership transfer is anchored on: a live agent (by
 *  agent_id) or a draft at any stage (by draft id). Either way the backend
 *  moves the whole lineage. */
export type TransferRef =
  | { kind: 'published'; agentId: string }
  | { kind: 'draft'; draftId: string };

export type TransferCheckStatus = 'pass' | 'warn' | 'fail' | 'info';

/** One readiness rule from the transfer rule engine (owner_transfer.py):
 *  active user, Studio role, required abilities, Agent Hub access, connector
 *  connections (automations), references to the old owner, lifecycle. `fail`
 *  blocks; `warn`/`info` are shown, never hidden. */
export interface TransferCheck {
  id: string;
  label: string;
  status: TransferCheckStatus;
  message: string;
  /** Rule-specific context: `abilities` (missing names), `references`
   *  ({path, match}), `not_connected`, `grant_added`, `transient`… */
  detail?: Record<string, unknown>;
}

/** One thing the transfer does — or cannot do by itself (follow_up). */
export interface TransferEffect {
  id: string;
  label: string;
  status: 'will_move' | 'not_applicable' | 'follow_up';
  message: string;
  detail?: Record<string, unknown>;
}

/** Result of POST …/transfer-owner — the same shape for a dry run (preview),
 *  a refusal (400, `ok: false` + the failing checks) and a commit. */
export interface TransferOwnerResult {
  agent_id: string;
  display_name?: string;
  kind?: 'chat' | 'automation' | string;
  owner: string;
  previous_owner?: string;
  revisions_transferred: number;
  /** Lineage records the transfer covers (preview) — revisions_transferred once committed. */
  revisions?: number;
  note?: string;
  message?: string;
  code?: string;
  abilities?: string[];
  /** True when the request only validated (dry_run); nothing was written. */
  dry_run?: boolean;
  /** No failing check. */
  ok?: boolean;
  checks?: TransferCheck[];
  effects?: TransferEffect[];
  follow_ups?: TransferEffect[];
  /** The agent's catalog (enforcement) owner was moved too. */
  catalog_updated?: boolean;
  /** The config-db agent-registry owner was moved too. */
  registry_updated?: boolean;
  /** An owner-private agent's access grant moved from the old to the new owner. */
  access_moved?: boolean;
  /** Live automation: a re-publish for the new owner was queued (or why not). */
  rerender?: { queued: boolean; message: string; draft_id?: string | null } | null;
  /** E-mail addresses the platform queue was asked to notify. */
  notified?: string[];
}

/** One recorded handover (AGENT_OWNER_HISTORY), newest first. */
export interface OwnerHistoryEntry {
  from_owner: string;
  to_owner: string;
  actor: string;
  actor_role: string;
  source: string;
  /** Epoch milliseconds. */
  ts: number;
  revisions?: number;
  catalog_updated?: boolean;
  access_moved?: boolean;
}

export interface OwnerHistory {
  agent_id: string;
  owner: string;
  history: OwnerHistoryEntry[];
}

export interface PublishedAgent {
  agent_id: string;
  display_name: string;
  description: string;
  department: string;
  /** Creator-chosen profile accent — keeps card identity continuous when an
   *  agent goes live (falls back to a hash color when absent). */
  color?: string;
  state: DraftState;
  pr1_url?: string;
  pr2_url?: string;
  last_activity: number;
  submitted_by: string;
  /** Current Studio owner (drafts created_by) — the identity a transfer moves.
   *  Falls back to submitted_by for rows that predate it. */
  owner?: string;
  approver?: string;
  error_context?: ErrorContext;
  /** Deploy targets (mirrors WizardInput.environments) for the env badge. */
  environments?: string[];
}

/** One card-safe row from GET /agents/directory — the cross-creator surface:
 *  live published Studio agents the CALLER can access (their own + ones
 *  granted via user/group allowed_agents; admins see all). Deliberately
 *  slimmer than PublishedAgent — no PR links, error context, or draft state
 *  beyond the published pair. */
export interface DirectoryAgent {
  agent_id: string;
  display_name: string;
  description: string;
  department: string;
  /** Directory rows are always published: live in dev, or promoted to prod. */
  state: 'live' | 'promoted';
  /** Creator username — rendered as "you" when is_owner. */
  owner: string;
  is_owner: boolean;
  published_at: number; // unix-seconds
}

export interface PublishResult {
  pr1_url: string;
  pr2_url: string;
  agent_id: string;
}

/** Server-generated warning shown in the test/preview surfaces when an agent
 *  equips write tools. Studio executes writes FOR REAL there (no sandbox), so
 *  this is the control that keeps that honest. Generated from catalog
 *  sensitivity, so new OAuth write connectors are covered automatically. */
export interface WriteActionNotice {
  /** Names of the equipped write tools. */
  tools: string[];
  details: Array<{ name: string; display_name: string; target: string; connector: string }>;
  /** Per-user OAuth connectors — writes here are attributed to the tester. */
  connectors: string[];
  /** Targets on a shared service credential — real, but not attributed. */
  service_targets: string[];
  /** Ready-to-render prose. */
  message: string;
}

export interface SmokeTestResult {
  response: string;
  took_ms: number;
  tool_calls: Array<{ name: string; input: unknown; output?: unknown; error?: string;
                     write?: boolean; connector?: string }>;
  error?: string;
  /** Present when the tested agent equips write tools — those calls ran for real. */
  write_notice?: WriteActionNotice | null;
}

/** The time-saved change a submit would make to an existing agent: a deploy
 *  writes the draft's value over the one currently stored in config-db. */
export interface RoiChange {
  agent_id: string;
  current_minutes: number;
  proposed_minutes: number;
}

export interface ValidationResult {
  ok: boolean;
  errors: Array<{ field: string; code: string; message: string }>;
  /** Same checks submit persists — pre-submit visibility in the modal. */
  checks?: SubmissionCheck[];
  /** Set when the draft's time saved differs from the agent's stored value
   *  (null/absent otherwise) — the submit modal asks the creator to confirm. */
  roi_change?: RoiChange | null;
}

/** Result of a live draft test-chat turn (builder preview panel).
 *  `history` is the provider-native message array — echo it back verbatim on
 *  the next call; the client never inspects it. */
export interface DraftTestChatResult extends SmokeTestResult {
  history?: unknown[];
}

/** One poll frame of an ASYNC live test-chat turn. `text` carries progress
 *  lines while running ("▸ search_confluence…"); the terminal frame is status
 *  'done' (with the full `result`) or 'failed' (with a user-safe `error`).
 *  The turn runs on a worker Lambda with a 300s budget — tool-heavy questions
 *  no longer die at API Gateway's ~29s ceiling. */
export interface TestChatStreamEvent {
  status: 'queued' | 'running' | 'done' | 'failed';
  text: string;
  result?: DraftTestChatResult;
  error?: string;
}

// ── Co-builder (conversational draft editing) ──────────────────────────
// POST /agents/drafts/{id}/ai-assist/cobuilder. The LLM proposes a patch;
// the SERVER enforces the field allowlist, drops no-op echoes, and resolves
// tool names against the gateway catalog. Sections arrive pre-merged over
// the SENT inputs — stale by one LLM round-trip — so the client three-way
// merges `patch` against the live draft (cobuilder-merge.ts) before applying
// it through the normal updateDraft path.

export interface CoBuilderTurn {
  who: 'user' | 'bot';
  text: string;
}

/** A document the creator attached to a co-builder turn (parsed client-side
 *  from a PDF/HTML/text file) — the model derives a report template from it. */
export interface CoBuilderDocument {
  name: string;
  text: string;
}

export interface CoBuilderResult {
  reply: string;
  patch: Partial<WizardInput> | null;
  section: 'identity' | 'instructions' | 'capabilities' | 'schedule' | 'access' | null;
  /** Set when the creator asked to (re)design the downloadable report. The
   *  cobuilder turn itself does NOT generate the template (that would mean two
   *  sequential model calls in one request → API Gateway 504); instead the
   *  frontend runs /ai-assist/report-template as a SECOND request with this
   *  description, then applies the result to meta.report. */
  report_template?: { description: string; format: 'html' | 'pdf' };
  /** Set for a TARGETED styling tweak (e.g. "make the table header red"): a small
   *  additive CSS rule the frontend APPENDS to the report's current theme,
   *  preserving the rest of the look — unlike report_template, which regenerates
   *  the whole theme. */
  report_css?: string;
  /** Set when the creator asked to (re)design the delivery EMAIL template
   *  (automations delivering by email / s3+email). Mirrors report_template: the
   *  cobuilder turn returns the description only; the frontend runs
   *  /ai-assist/email-template as a SECOND request, then applies the result to
   *  meta.email. */
  email_template?: { description: string };
  /** Set for a TARGETED email styling tweak (e.g. "make the header darker"): a
   *  small additive CSS rule the frontend APPENDS to the email's current theme,
   *  mirroring report_css. */
  email_css?: string;
  /** Guided build: when the reply ends with a follow-up question (the model
   *  interviews the creator toward readiness), 2-4 short quick-reply answers
   *  the UI renders as tappable chips. Server-sanitized (≤4, ≤60 chars each). */
  suggestions?: string[];
  /** Self-test: run the draft agent once with this sample message (a SECOND
   *  request — POST /drafts/{id}/test-chat — so the demo never shares the
   *  cobuilder turn's API Gateway budget) and show the agent's real reply in
   *  the conversation. Chat agents only (server-enforced); automations demo
   *  through the Test panel's preview run. */
  test_chat?: { message: string };
  /** Automation self-test: kick ONE real preview run of the scheduled task
   *  (write tools blocked, no delivery) and show the report in the chat when
   *  it lands. Automations only (server-enforced). */
  test_run?: boolean;
}

/** One persisted co-builder transcript message — the durable core of CoMsg
 *  (undo snapshots and retry state are session concepts and never persist). */
export interface CoBuilderChatMessage {
  who: 'user' | 'bot';
  text: string;
  /** True when the bubble is the DRAFT AGENT's own test output, not a
   *  co-builder message. */
  agent?: boolean;
}

/** One poll frame of a STREAMING co-builder turn. `text` is the model's reply
 *  so far (grows while running); the terminal frame is status 'done' (carrying
 *  the full `result`) or 'failed' (carrying a user-safe `error`). The turn runs
 *  on an async worker Lambda — no API Gateway 29s ceiling — and the client
 *  polls GET .../cobuilder-stream/{stream_id} every ~500ms. */
export interface CoBuilderStreamEvent {
  status: 'queued' | 'running' | 'done' | 'failed';
  text: string;
  result?: CoBuilderResult;
  error?: string;
}

// ── Service interface ──────────────────────────────────────────────────

export interface IAgentBuilderService {
  // Drafts
  listDrafts(): Observable<Draft[]>;
  getDraft(id: string): Observable<Draft>;
  createDraft(initial?: Partial<WizardInput>): Observable<Draft>;
  updateDraft(id: string, patch: Partial<WizardInput>, expectedVersion: number): Observable<Draft>;
  deleteDraft(id: string): Observable<void>;
  validateDraft(id: string): Observable<ValidationResult>;
  previewDraft(id: string): Observable<Record<string, string>>; // path -> content

  // Approval workflow
  submitDraft(id: string): Observable<Draft>;
  withdrawDraft(id: string): Observable<Draft>;
  listApprovals(): Observable<Draft[]>;
  approveDraft(id: string, comment: string): Observable<Draft>;
  rejectDraft(id: string, comment: string): Observable<Draft>;

  // Publishing + lifecycle
  publishDraft(id: string): Observable<PublishResult>;
  retryPublish(id: string): Observable<PublishResult>;
  /** W5: advance pr_open → building → live (server checks the PR merge +
   *  deploy-published SSM ARN; throttled server-side, safe to poll). */
  refreshStatus(id: string): Observable<Draft>;
  markResolvedManually(id: string): Observable<Draft>;
  /** Promote a live agent to prod (admin): opens the prod PRs (PR1 → runtimes
   *  main, PR2 → prod-template) and records prod in the agent's environments. */
  promoteToProd(id: string): Observable<Draft>;
  listPublished(): Observable<PublishedAgent[]>;
  /** Team directory (GET /agents/directory): live published agents the caller
   *  can access — their own + ones shared with them; admins see all. */
  listDirectory(): Observable<DirectoryAgent[]>;
  getPublishedDraft(agentId: string): Observable<Draft>;
  editPublished(agentId: string): Observable<Draft>;
  deletePublished(agentId: string): Observable<void>;
  /** Hand an agent (live, or a draft at any stage) to a new owner —
   *  admin/super_admin or the agent's creator. One all-or-nothing, lineage-
   *  wide change: Studio ownership (My Agents + edit rights), the catalog and
   *  registry owner, Agent Hub access, run attribution, history; a live
   *  automation is re-published for the new owner and both owners are mailed.
   *  `dryRun` runs every readiness rule and writes nothing — the preview the
   *  dialog shows before confirming. A refusal resolves (not throws) with
   *  `ok: false` and the failing checks so the UI can render them. */
  transferOwner(ref: TransferRef, newOwner: string, dryRun?: boolean): Observable<TransferOwnerResult>;
  /** Who owned a live agent when (owner badge / history). */
  getOwnerHistory(agentId: string): Observable<OwnerHistory>;

  // Gateway tool catalog (wizard Tools-step picker)
  listTools(): Observable<GatewayTarget[]>;
  /** Full GET /tools payload: targets + the server-owned `presentation`
   *  arrangement contract. Surfaces that arrange categories (the Abilities
   *  library) use this; listTools() stays for callers that only need tools. */
  listToolCatalog(): Observable<ToolCatalogResult>;

  /** Server-owned report starter presets (GET /report-templates). An empty
   *  list means the server has none published — callers fall back to the
   *  bundled REPORT_TEMPLATE_PRESETS copy. */
  listReportTemplates(): Observable<ReportTemplatePreset[]>;

  // Share-target group directory (wizard Access-step picker). id + display only.
  listGroups(): Observable<GroupOption[]>;

  // Abilities library: the curated roadmap of not-yet-live connectors, and a
  // way to register demand for a gap.
  listComingSoon(): Observable<ComingSoonAbility[]>;
  requestAbility(req: AbilityRequest): Observable<{ status: string }>;

  // AI-assist.
  // `equippedAbilities` (un-prefixed operationIds): when supplied (the
  // abilities-first flow), the model treats them as ALREADY equipped and
  // grounds role/instructions/name on them instead of choosing freely.
  aiAssistPrompt(
    id: string,
    description: string,
    equippedAbilities?: string[],
  ): Observable<WizardPrompt>;
  aiAssistTool(id: string, intent: string): Observable<AnthropicToolSchema>;
  /** Generate a full downloadable-report template (title, sections, Markdown
   *  skeleton, CSS theme) from the creator's plain-English format description. */
  aiAssistReportTemplate(
    id: string,
    description: string,
    format?: 'html' | 'pdf' | 'xlsx' | 'pptx',
  ): Observable<ReportTemplateResult>;
  /** Generate a full delivery-email template (subject, Markdown body skeleton,
   *  CSS theme) from the creator's plain-English description. Mirrors
   *  aiAssistReportTemplate; applied to meta.email. */
  aiAssistEmailTemplate(
    id: string,
    description: string,
  ): Observable<EmailTemplateResult>;

  // Co-builder: conversational edits to the draft. `inputs` carries the
  // current (possibly unsaved) wizard state; `history` gives the model the
  // recent conversation for follow-ups ("actually make it shorter").
  // `document` (optional) is a parsed file the creator attached — the model
  // derives a report template (meta.report) from it. `readiness` (optional)
  // lists the wizard's unmet submission-checklist items — it drives the
  // guided-build follow-up question the model appends to its reply.
  // `testOutput` (optional) is the agent's latest test-chat reply — the model
  // treats it as ground truth when the creator critiques what the agent
  // actually produced.
  coBuilder(
    id: string,
    message: string,
    inputs: WizardInput,
    history: CoBuilderTurn[],
    document?: CoBuilderDocument,
    readiness?: string[],
    testOutput?: string,
  ): Observable<CoBuilderResult>;

  // Streaming variant: same contract as coBuilder minus `document` (document
  // turns stay on the sync route — a fast Haiku call), emitting the reply as
  // it generates and finishing with the full result.
  coBuilderStream(
    id: string,
    message: string,
    inputs: WizardInput,
    history: CoBuilderTurn[],
    readiness?: string[],
    testOutput?: string,
  ): Observable<CoBuilderStreamEvent>;

  // Co-builder chat persistence: the transcript survives page reloads (one
  // record per draft+user server-side, 30-day TTL). Save replaces the whole
  // transcript; the server sanitizes and caps it.
  getCoBuilderChat(id: string): Observable<CoBuilderChatMessage[]>;
  saveCoBuilderChat(id: string, messages: CoBuilderChatMessage[]): Observable<CoBuilderChatMessage[]>;

  // Knowledge base (draft-scoped RAG documents — shape.has_knowledge_base).
  /** Current documents + quota for the draft's knowledge base. Poll this
   *  while any document is queued/ingesting; statuses settle server-side. */
  listKnowledgeDocs(draftId: string): Observable<KnowledgeDocumentList>;
  /** Full upload pipeline: mint a presigned ticket → PUT the file bytes
   *  straight to S3 (bare client — no Authorization header) → confirm.
   *  Emits the CONFIRMED document (typically status 'queued'); ingestion
   *  progress arrives via listKnowledgeDocs polling. `onProgress` reports
   *  the S3 PUT's byte progress as a 0..1 fraction. */
  uploadKnowledgeFile(
    draftId: string,
    file: File,
    onProgress?: (fraction: number) => void,
  ): Observable<KnowledgeDocument>;
  deleteKnowledgeDoc(draftId: string, docId: string): Observable<void>;

  // Smoke test
  smokeTest(agentId: string, message: string): Observable<SmokeTestResult>;

  // Live draft test-chat (builder preview panel). `inputs` carries the current
  // (possibly unsaved) wizard state; `history` echoes the previous result's.
  testChatDraft(
    id: string,
    message: string,
    history: unknown[],
    inputs?: WizardInput,
  ): Observable<DraftTestChatResult>;

  // Async variant: the turn runs on a worker Lambda (300s budget) and this
  // emits progress + the final result. Falls back to the synchronous route
  // when streaming is unavailable.
  testChatDraftStream(
    id: string,
    message: string,
    history: unknown[],
    inputs?: WizardInput,
  ): Observable<TestChatStreamEvent>;

  // Complex agents: queue an async codegen run (202). The result lands on the
  // draft's `codegen` attribute — poll getDraft while queued/running.
  // `feedback` regenerates: plain-language change request against the
  // previously generated files.
  generateCode(id: string, feedback?: string): Observable<DraftCodegen>;

  // Automation preview run (drafts): queue one real task execution (202).
  // The result lands on the draft's `test_run` attribute — poll getDraft.
  testRunDraft(id: string): Observable<DraftTestRun>;

  // Automation operations on LIVE agents (published surface).
  listRuns(agentId: string): Observable<AgentRunsResult>;
  /** Fires one real production-path run via the agent's trigger Lambda
   *  (202; the run appears in the run history as user:<caller>). */
  runNow(agentId: string): Observable<{ accepted: boolean; message?: string }>;
  /** Pause/resume the schedule (the named EventBridge rule). */
  setScheduleState(agentId: string, enabled: boolean): Observable<{ schedule_state: ScheduleState }>;
  /** Re-presign a run's report (GET .../runs/{run_id}/report-url). Run report
   *  links are short-lived presigned S3 URLs, so an old run's `report_url`
   *  expires; this mints a fresh one on demand for the "Get fresh link"
   *  action. Returns { url }. */
  refreshReportUrl(agentId: string, runId: string): Observable<{ url: string }>;
}

export class ConflictError extends Error {
  constructor(message: string, public currentVersion: number, public currentState: DraftState) {
    super(message);
    this.name = 'ConflictError';
  }
}

export class NotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotFoundError';
  }
}
